import { v4 as uuid } from 'uuid';
import { map } from 'lodash';
import { toUUID } from '../../util/transform';
import { epoch, formatEpochAsDate, formatEpochAsTime, utc } from '../../util/datetime';
import type { ILiability } from './Liability.interface';
import { LiabilityType } from './Liability.enum';
import { CreditCardType } from '../global/CreditCardType.enum';
import Ownership from './Ownership';

export default class Liability implements ILiability {
  id: string;
  idReference: string;
  liabilityType: LiabilityType;
  name: string;
  lenderOtherName: string;
  bsb: string;
  accountNumber: string;
  interestRate: number;
  limit: number;
  balance: number;
  repayment: number;
  repaymentType: boolean;
  mortgageType: boolean;
  idAsset: string;
  creditCardType: CreditCardType;
  creditCardNumber: number;
  interestOnlyExpiryDate: number;
  fixedExpiryDate: number;
  endDate: number;
  asAtDate: number;
  isRefinanced: boolean;
  toBePaidOut: boolean;
  ownership: Array<Ownership>;
  timeStamp: number;
  isActive: boolean;

  getName() {
    return this.name || 'N/A';
  }

  getLiabilityTypeName(): boolean {
    return this.liabilityType.id === LiabilityType.available()[0].id
      ? `${this.liabilityType.title}/${this.mortgageType ? 'Investment' : 'Owner occupied'}`
      : this.liabilityType.title;
  }

  getEndDate(timeZone, locale, localeFormat) {
    return this.endDate
      ? formatEpochAsDate(this.endDate, timeZone, locale, localeFormat)
      : 'N/A';
  }

  getAsAtDate(timeZone, locale, localeFormat) {
    return this.asAtDate
      ? formatEpochAsDate(this.asAtDate, timeZone, locale, localeFormat)
      : 'N/A';
  }

  getInterestOnlyExpiryDate(timeZone, locale, localeFormat) {
    return this.interestOnlyExpiryDate
      ? formatEpochAsDate(this.interestOnlyExpiryDate, timeZone, locale, localeFormat)
      : 'N/A';
  }

  getFixedExpiryDate(timeZone, locale, localeFormat) {
    return this.fixedExpiryDate
      ? formatEpochAsDate(this.fixedExpiryDate, timeZone, locale, localeFormat)
      : 'N/A';
  }

  getDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getTime(timeZone, locale, localeFormat) {
    return formatEpochAsTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  static getAvailableLiabilityTypes = () => LiabilityType.available();

  static build = (data: ILiability) => new Liability(data);
  static blank = (liabilityType: LiabilityType, ownership: number) =>
    new Liability({ id: uuid(), liabilityType, ownership, isActive: true });

  /**
   * Constructor
   * @param {ILiability} data
   */
  constructor(data?: ILiability) {
    this.id = toUUID(data?.id) || uuid();
    // TODO: idTicketLiability is legacy structure
    this.idReference = toUUID(data?.idReference || data?.idTicketLiability);
    this.liabilityType = LiabilityType.of(data?.liabilityType?.id || data?.liabilityType?.name);
    this.name = data?.name || '';
    this.lenderOtherName = data?.lenderOtherName || '';
    this.bsb = data?.bsb || '';
    this.accountNumber = data?.accountNumber || '';
    this.interestRate = parseFloat(data?.interestRate || 0);
    this.limit = parseFloat(data?.limit || 0);
    this.balance = parseFloat(data?.balance || 0);
    this.repayment = parseFloat(data?.repayment || 0);
    this.repaymentType = data?.repaymentType === true;
    this.mortgageType = data?.mortgageType === true;
    this.idAsset = toUUID(data?.idAsset);
    this.creditCardType = CreditCardType.of(
      data?.creditCardType?.name || data?.creditCardType?.toString()
    );
    this.creditCardNumber = parseInt(data?.creditCardNumber || 0, 10);
    this.interestOnlyExpiryDate = data?.interestOnlyExpiryDate
      ? epoch(utc(data?.interestOnlyExpiryDate))
      : 0;
    this.fixedExpiryDate = data?.fixedExpiryDate ? epoch(utc(data?.fixedExpiryDate)) : 0;
    this.endDate = data?.endDate ? epoch(utc(data?.endDate)) : 0;
    this.asAtDate = data?.asAtDate ? epoch(utc(data?.asAtDate)) : 0;
    this.isRefinanced = data?.isRefinanced === true;
    this.toBePaidOut = data?.toBePaidOut === true;
    this.ownership = map(data?.ownership, (obj) => new Ownership(obj));
    this.timeStamp = epoch(utc(data?.timeStamp));
    this.isActive = data?.isActive === true;
  }
}
